import { ConnectProto } from "@racoon/api";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { fromTimestamp } from "@racoon/lib";
import { format, isSameMinute } from "date-fns";
import { useListWhatsappMessagesQuery } from "~/shared/api/whatsapp/whatsapp.grpc";
import { roleLabel } from "~/shared/lib/role-options";
import { Person } from "~/shared/ui/person";
import { getUserName } from "../model/orders.helpers";
import { useWhatsappDialogueModal } from "../hooks/use-whatsapp-dialogue-modal";

const labelClass = "text-text-subtle";

/**
 * Who created the job and who saved it last. For a WhatsApp bot job (a system user's) the
 * author is the dialogue's contact.
 */
export const JobCardAuthor = ({ job }: { job: ConnectProto.Job }) => {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  const { openWhatsappDialogue } = useWhatsappDialogueModal();

  const isWhatsapp = !!job.whatsappAuthorId;
  const { data: dialogue } = useListWhatsappMessagesQuery({ jobId: job.id }, { skip: !isWhatsapp });
  // By value: ids in the store are strings, whatever their type says (see normalizeBigInt)
  const contact = dialogue?.messages.find(
    (m) => String(m.contact?.id) === String(job.whatsappAuthorId),
  )?.contact;

  const name = isWhatsapp ? (contact?.name ?? "") : getUserName(job.author);
  const role = isWhatsapp ? contact?.position : roleLabel(t, job.author?.role);
  const createdAt = fromTimestamp(job.createdAt);
  // updated_at matches created_at until the job's first write. A save by nobody within the
  // minute it was created, the system finishing its setup, isn't an edit; a person's always is.
  const updatedAt = fromTimestamp(job.updatedAt);
  const isEdited =
    !!updatedAt && !!createdAt && (!!job.updatedBy || !isSameMinute(updatedAt, createdAt));
  const lastUpdate = isEdited ? updatedAt : null;

  const renderDateTime = (date: Date) => (
    <span className="flex flex-shrink-0 gap-2">
      <span>{format(date, "HH:mm")}</span>
      <span>{format(date, "d MMM yyyy", { locale: dateFnsLocale })}</span>
    </span>
  );

  return (
    // A fixed label column, so every value starts at the same x
    <div className="grid grid-cols-[100px_1fr] items-center gap-x-3 gap-y-4 rounded-lg border border-border-primary bg-slate-100 p-4 text-sm">
      <span className={labelClass}>{t(Namespace.Orders, "columns.author")}</span>
      <div className="flex min-w-0 items-center gap-3">
        {/* A WhatsApp contact's name arrives with the dialogue */}
        <Person name={name} role={role} isClient={job.isCreatedByClient} testId="job-card-author" />
        {createdAt && <span className="ml-auto text-text-subtle">{renderDateTime(createdAt)}</span>}
      </div>
      {isWhatsapp && (
        <>
          <span className={labelClass}>{t(Namespace.Orders, "card.method")}</span>
          <div className="flex min-w-0 items-center gap-3">
            <span className="truncate text-text-body" data-testid="job-card-method">
              {t(Namespace.Orders, "card.whatsappBot")}
            </span>
            <button
              type="button"
              className="ml-auto flex-shrink-0 text-text-information"
              data-testid="job-card-chat-history"
              onClick={() => openWhatsappDialogue(job)}
            >
              {t(Namespace.Orders, "card.chatHistory")}
            </button>
          </div>
        </>
      )}
      {lastUpdate && (
        <>
          <span className={labelClass}>{t(Namespace.Orders, "card.updatedAt")}</span>
          <div className="flex min-w-0 items-center gap-3">
            {/* Unset when the last save was the system's (generator, AI dispatcher) */}
            {job.updatedBy && (
              <Person
                name={getUserName(job.updatedBy)}
                role={roleLabel(t, job.updatedBy.role)}
                testId="job-card-editor"
              />
            )}
            <span className="ml-auto text-text-subtle" data-testid="job-card-updated">
              {renderDateTime(lastUpdate)}
            </span>
          </div>
        </>
      )}
    </div>
  );
};
